import React, { useEffect, useRef, useState } from "react";
import { InlineMath } from "react-katex";
import SetKeyboard from "./SetKeyboard";

/* ====== Stałe layoutu (spójne z FractionInput) ====== */
const INDENT = 36;               // px – kolumna na ikonkę + odstęp
const PANEL_WIDTH_DESKTOP = 250; // px – szerokość panelu (bez INDENT)

/**
 * Parser wejścia typu "2*2*3*5" / "2·2·3·5" / "2 x 2 x 3 x 5"
 * Zwraca:
 *  - undefined   -> puste (brak odpowiedzi)
 *  - null        -> niepoprawny format (np. litery lub ujemne)
 *  - number[]    -> posortowana, zduplikowana lista czynników (np. [2,2,3,5])
 */
export function parseFactorProductInput(value) {
  if (value == null) return undefined;
  const raw = String(value).trim();
  if (raw === "") return undefined;

  // Normalizacja separatorów na '*'
  const cleaned = raw
    .replace(/·|⋅|x|X|✕|×/g, "*")
    .replace(/\s+/g, "")
    .replace(/,+/g, "*")
    .replace(/\*{2,}/g, "*")
    .replace(/^\*|\*$/g, "");

  if (cleaned === "") return undefined;

  const parts = cleaned.split("*");
  // tylko dodatnie liczby całkowite >= 2
  const nums = [];
  for (const p of parts) {
    if (!/^\d+$/.test(p)) return null;
    const n = Number(p);
    if (!Number.isFinite(n) || n < 2) return null;
    nums.push(n);
  }
  nums.sort((a, b) => a - b);
  return nums;
}

export function factorArrayToLatex(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return "\\varnothing";
  return arr.join("\\cdot");
}

/**
 * FactorProductInput – wersja ze spójną stylistyką i klawiaturą SetKeyboard
 * Props:
 *  - value: string | { raw, parsed }
 *  - onChange: (obj) => void, gdzie obj = { raw: string, parsed: number[]|null|undefined }
 *  - disabled?: boolean
 *  - placeholder?: string
 *  - ariaLabel?: string
 *  - showPreview?: boolean
 *  - panelWidthDesktop?: number
 *  - indentPx?: number
 */
export default function FactorProductInput({
  value,
  onChange,
  disabled,
  placeholder = "np. 2·2·3·5",
  ariaLabel = "iloczyn czynników",
  showPreview = true,
  panelWidthDesktop = PANEL_WIDTH_DESKTOP,
  indentPx = INDENT,
}) {
  const pickString = (v) =>
    v && typeof v === "object" && "raw" in v ? (v.raw ?? "") : (v ?? "");

  const [local, setLocal] = useState(pickString(value));
  const [kbdOpen, setKbdOpen] = useState(false);
  const inputRef = useRef(null);

  // synchronizacja z value zewnętrznym
  useEffect(() => { setLocal(pickString(value)); }, [value]);

  // emit onChange w tym samym kształcie co wcześniej
  useEffect(() => {
    const parsed = parseFactorProductInput(local);
    onChange && onChange({ raw: local, parsed });
  }, [local]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ===== caret helpers (jak we FractionInput) ===== */
  const setCaret = (pos) => {
    const el = inputRef.current; if (!el) return;
    el.focus(); el.setSelectionRange(pos, pos);
  };
  const insertAtCursor = (text) => {
    const el = inputRef.current; if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const next = local.slice(0, s) + text + local.slice(e);
    setLocal(next);
    setTimeout(() => setCaret(s + text.length), 0);
  };

  // opcjonalne, zostawiamy dla spójności API z SetKeyboard
  const handleKbdAction = (type) => { if (type === "insert") return; };

  /* ====== Podgląd ====== */
  const parsed = parseFactorProductInput(local);
  let previewNode = null;
  if (showPreview && local) {
    if (parsed === undefined) previewNode = <i>(brak odpowiedzi)</i>;
    else if (parsed === null) previewNode = <i>(niepoprawny zapis)</i>;
    else previewNode = <InlineMath math={factorArrayToLatex(parsed)} />;
  }

  /* ===== Layout: input szeroki jak panel ===== */
  const containerStyle = {
    width: "100%",
    maxWidth: panelWidthDesktop + indentPx, // panel + kolumna ikony
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-start",
  };
  const inputStyle = { width: `calc(100% - ${indentPx}px)`, minWidth: 0, flex: "0 0 auto" };

  return (
    <div style={containerStyle}>
      {/* wiersz: [ikona][pole] */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${indentPx}px 1fr`,
          columnGap: 0,
          alignItems: "center",
          width: "100%",
        }}
      >
        <SetKeyboard
          render="toggle"
          targetRef={inputRef}
          open={kbdOpen}
          onOpenChange={setKbdOpen}
          onAction={handleKbdAction}
        />
        <input
          ref={inputRef}
          type="text"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="input-secondary"
          style={inputStyle}
        />
      </div>

      {kbdOpen && (
        <div style={{ width: "100%" }}>
          {/* panel klawiatury odsunięty o ten sam INDENT – idealnie pod inputem */}
          <SetKeyboard
            render="panel"
            targetRef={inputRef}
            open={kbdOpen}
            onOpenChange={setKbdOpen}
            onAction={(type, payload) => {
              if (type === "insert") insertAtCursor(String(payload ?? ""));
              if (type === "backspace") {
                const el = inputRef.current; if (!el) return;
                const s = el.selectionStart, e = el.selectionEnd;
                if (s !== e) {
                  const next = local.slice(0, s) + local.slice(e);
                  setLocal(next); setTimeout(() => setCaret(s), 0);
                } else if (s > 0) {
                  const next = local.slice(0, s - 1) + local.slice(e);
                  setLocal(next); setTimeout(() => setCaret(s - 1), 0);
                }
                return;
              }
              if (type === "moveLeft")  { const el = inputRef.current; const s = el.selectionStart, e = el.selectionEnd; setTimeout(() => setCaret(Math.max(0, Math.min(s, e) - 1)), 0); }
              if (type === "moveRight") { const el = inputRef.current; const s = el.selectionStart, e = el.selectionEnd; setTimeout(() => setCaret(Math.min(local.length, Math.max(s, e) + 1)), 0); }
              if (type === "clear")     { setLocal(""); setTimeout(() => setCaret(0), 0); }
            }}
            compact
            panelIndent={INDENT}
          />
        </div>
      )}

      {showPreview && local && (
        <div style={{ marginTop: 6, marginBottom: 0, pointerEvents: "none" }}>
          <strong>Podgląd:</strong>{" "}
          {previewNode ?? <InlineMath math={'\\varnothing'} />}
        </div>
      )}
    </div>
  );
}
